import localhost from "../../../apis/localhost";
import { SHAREPOINT_ERROR_CODES } from "../../../constants/sharepointAccess";
import {
  selectSharepointAccessResults,
  selectSharepointData,
  selectSharepointNextSkipToken,
} from "../selectors";
import {
  resetSharepointState,
  setNextSkipToken,
  setSharepointData,
  setSharepointError,
  setSharepointLoading,
} from "../sharepointSlice";
import { checkSiteAccess } from "./checkSiteAccess";
import { CONNECTIVITY_ERROR, LIMIT, SKIP_REDIRECT } from "./constants";
import { requireSharepointSignIn } from "./policyErrors";
import {
  applyPolicyInfo,
  beginRequest,
  errorCodeOf,
  isCurrentRequest,
  redirectUnlessSharepointError,
  statusOf,
  withPendingAccess,
} from "./utils";

export const fetchSharepointSites = () => async (dispatch, getState) => {
  const requestId = beginRequest(dispatch, getState);
  try {
    dispatch(setSharepointError(null));
    const nextSkipToken = selectSharepointNextSkipToken(getState());

    if (!nextSkipToken) {
      dispatch(setSharepointLoading(true));
    }

    const response = await localhost.get("/sharepoint/sites", {
      params: { nextSkipToken, limit: LIMIT },
      ...SKIP_REDIRECT,
    });

    if (!isCurrentRequest(getState, requestId)) {
      return;
    }

    if (response.status !== 200) {
      const error = new Error();
      error.code = response.status;
      throw error;
    }

    const {
      sites = [],
      pagination,
      restricted = false,
      policyRevision = null,
    } = response.data;
    const changed = applyPolicyInfo(
      dispatch,
      getState,
      restricted,
      policyRevision
    );
    const isNextPage = Boolean(nextSkipToken) && !restricted && !changed;
    const existingSites = isNextPage
      ? selectSharepointData(getState()) || []
      : [];

    const sitesWithType = withPendingAccess(
      [...existingSites, ...sites].map((site) => ({ ...site, type: "site" })),
      restricted,
      selectSharepointAccessResults(getState())
    );

    dispatch(setSharepointData(sitesWithType));
    dispatch(setNextSkipToken(restricted ? null : pagination?.nextSkipToken));
    if (restricted) {
      dispatch(checkSiteAccess());
    }
  } catch (error) {
    if (!isCurrentRequest(getState, requestId)) {
      return;
    }
    if (errorCodeOf(error) === SHAREPOINT_ERROR_CODES.AUTH_REQUIRED) {
      requireSharepointSignIn(dispatch);
      return;
    }
    redirectUnlessSharepointError(error);
    const code = statusOf(error);
    if (code >= 500 && code < 600) {
      dispatch(setSharepointError(new Error(CONNECTIVITY_ERROR)));
    }
    dispatch(resetSharepointState());
  } finally {
    if (isCurrentRequest(getState, requestId)) {
      dispatch(setSharepointLoading(false));
    }
  }
};
